import { useMemo, useState, useRef, useEffect } from 'react';
import {
  PromotionalBanner,
  useTranslation,
  Drawer,
  PromotionalMessageTextSize,
  PromotionalMessageType,
  Tabs,
  TabDesign,
  TabLayout,
} from '@nextgen-web-framework/all';
import { formatDate } from '@commerce-promotion-webcomponents/sdk';
import { PromotionDetailsListProps } from '../PromotionDetailsList/types/propsTypes';
import styles from './PromotionDetailsTabVariant.module.scss';

const PromotionDetailsTabVariant = ({
  firedPromotions,
  potentialPromotions,
  couponVariant,
  orderPageVariant,
  closeDrawer,
}: PromotionDetailsListProps) => {
  const [isScrollTouched, setIsScrollTouched] = useState<boolean>(false);
  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const [selectedTab, setSelectedTab] = useState<PromotionalMessageType>(() =>
    firedPromotions?.length ? PromotionalMessageType.APPLIED : PromotionalMessageType.POTENTIAL,
  );
  const t = useTranslation();
  const offerEndsText = t('promotion.promotionDetailsList.offerEnds');
  const headerTitle = orderPageVariant
    ? t(couponVariant ? 'promotion.promotionDetailsList.coupons' : 'promotion.promotionDetailsList.promotions')
    : t('promotion.promotionDetailsList.promoDetailsText');

  const tabList = [
    {
      id: PromotionalMessageType.APPLIED,
      title: t('promotion.promotionDetailsList.promotionsAppliedText').replace(
        '{{text}}',
        `${Number(firedPromotions?.length ?? 0)}`,
      ),
      disabled: !firedPromotions || firedPromotions.length === 0,
      items: firedPromotions,
    },
    {
      id: PromotionalMessageType.POTENTIAL,
      title: t('promotion.promotionDetailsList.promotionsAvailableText').replace(
        '{{text}}',
        `${Number(potentialPromotions?.length ?? 0)}`,
      ),
      disabled: !potentialPromotions || potentialPromotions.length === 0,
      items: potentialPromotions,
    },
  ];

  const onScroll = () => {
    if (scrollableDivRef.current?.parentElement) {
      const { scrollTop } = scrollableDivRef.current.parentElement;
      if (scrollTop >= 15) {
        setIsScrollTouched(true);
      } else {
        setIsScrollTouched(false);
      }
    }
  };

  useEffect(() => {
    const scrollableDiv = scrollableDivRef.current;
    if (scrollableDiv?.parentElement) {
      scrollableDiv?.parentElement.addEventListener('scroll', onScroll);
    }
    return () => {
      if (scrollableDiv?.parentElement) {
        scrollableDiv?.parentElement.removeEventListener('scroll', onScroll);
      }
    };
  }, []);

  const selectedTabIndex = useMemo(() => tabList.findIndex(({ id }) => id === selectedTab), [selectedTab]);
  return (
    <Drawer
      bottomGap={0}
      header={<span data-testid="promo-drawer-title">{headerTitle}</span>}
      closeIcon
      closeOnBackdropClick
      onClose={closeDrawer}
      className={styles['promotion-tab-drawer']}
    >
      <div className={styles['promotion-details-tab-variant-drawer-content']} ref={scrollableDivRef}>
        <Tabs
          className={`${styles['tabs-container']} ${isScrollTouched && styles['scrolled']}`}
          tabList={tabList}
          design={TabDesign.PILL}
          layout={TabLayout.HORIZONTAL}
          selectedTab={selectedTabIndex}
          onTabClick={(tab) => setSelectedTab(tabList[tab].id)}
        />
        <div className={styles['tabs-content']} data-testid={`${tabList[selectedTabIndex].id}-promos`}>
          {tabList[selectedTabIndex]?.items?.map(({ promotion }) => (
            <PromotionalBanner
              key={promotion?.code}
              isExpanded
              promotionType={tabList[selectedTabIndex].id}
              iconName={couponVariant ? 'ticket' : 'tags'}
              promotionTitleText={promotion?.name ?? ''}
              size={PromotionalMessageTextSize.LARGE}
              {...(!orderPageVariant && {
                promotionlDescription: promotion?.message,
                promotionEndText:
                  promotion?.endDate && `${offerEndsText} ${formatDate(promotion?.endDate, false, true)}`,
              })}
            />
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export default PromotionDetailsTabVariant;
